import {
    Component,
    OnInit,
    Signal,
    computed,
    inject,
    ChangeDetectorRef,
    AfterViewChecked,
    DestroyRef,
} from '@angular/core';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { LifecycleRulesetStore } from '../store/lifecycle-ruleset.store';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LifecycleRulesRecordSelectionForm } from '@r1-compass-library/models/lifecycle-rules/lifecycle-rules-form.model';
import { filterNil } from 'ngxtension/filter-nil';
import { DividerComponent, GroupingLogicComplexComponent, InstructionsComponent } from '@r1-compass-library/components';
import { GenericSelectionCriterionComponent } from '../../template-criteria/template-criteria-builder/criteria/generic-selection-criterion/generic-selection-criterion.component';
import {
    GenericSelectionCriterionForm,
    convertGenericSelectionCriterionForm,
} from '@r1-compass-library/models/criteria/selection-criteria-form.model';
import { MatButtonModule } from '@angular/material/button';
import { GenericSelectionCriterionData } from '@r1-compass-library/models';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { DataMartLifecyclesTableStore } from '../store/data-mart-lifecycles.store';
import { take, filter, combineLatest, BehaviorSubject } from 'rxjs';
import {
    FormUnsavedChangesWarningEntity,
    noopFormSaveMutationResult,
    wasSaveSuccessful,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { AssetInvalidSaveWarningService } from '../../asset-invalid-save-warning.service';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { LifecycleRulesValidationService, LifecycleRulesSectionFormMap } from '../lifecycle-rules-validation.service';
import { LifecycleRulesValidationStateService } from '../lifecycle-rules-validation-state.service';
import {
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
    LifecycleRulesSectionName,
} from '@r1-compass-library/features/validation-sync';

@Component({
    selector: 'r1-compass-app-lifecycle-rules-record-selection',
    templateUrl: './lifecycle-rules-record-selection.component.html',
    styleUrl: './lifecycle-rules-record-selection.component.scss',
    imports: [
        DividerComponent,
        GenericSelectionCriterionComponent,
        GroupingLogicComplexComponent,
        InstructionsComponent,
        MatButtonModule,
        ReactiveFormsModule,
    ],
})
export class LifecycleRulesRecordSelectionComponent
    implements
        OnInit,
        AfterViewChecked,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<LifecycleRulesSectionName, LifecycleRuleset>,
        AfterViewChecked
{
    readonly #route = inject(ActivatedRoute);
    readonly #rulesetStore = inject(LifecycleRulesetStore);
    readonly #lifecyclesStore = inject(DataMartLifecyclesTableStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #changeDetectorRef = inject(ChangeDetectorRef);
    readonly #destroyRef$ = inject(DestroyRef);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #validationService = inject(LifecycleRulesValidationService);
    readonly #validationStateService = inject(LifecycleRulesValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #invalidSaveWarningService = inject(AssetInvalidSaveWarningService);

    recordSelectionForm = new FormGroup<LifecycleRulesRecordSelectionForm>(new LifecycleRulesRecordSelectionForm());

    lifecycleRulesTables = this.#lifecyclesStore.tables;
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    private lifecycleRulesTables$ = toObservable(this.lifecycleRulesTables);

    readonly canDeactivate$ = new BehaviorSubject({ dirtied: false, valid: false });
    readonly saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningMisc;

    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.#rulesetStore.lifecycleRuleset(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'recordSelection'
        )
    );

    constructor() {
        this.saveGuardHandlerFn({
            canDeactivate$: this.canDeactivate$,
            disableSave: this.disableSave,
        });
    }

    handleFormSaveWarningAfterInit() {
        this.#formSaveWarningStore.formUnsavedChangesWarningAfterNgOnInit({
            form: this.recordSelectionForm,
            canDeactivate$: this.canDeactivate$,
            destroyRef$: this.#destroyRef$,
            disableSave: this.disableSave,
        });
    }

    private selectedIamDivision$ = toObservable(this.#globalLoggedInUserStore.selectedIamDivision);

    ngOnInit(): void {
        const rulesetId: string = this.#route.snapshot.params['id'];
        this.selectedIamDivision$.pipe(filterNil()).subscribe(selectedIamDivision => {
            this.#lifecyclesStore.getTables();
        });

        if (rulesetId && rulesetId !== 'new') {
            const tables$ = this.lifecycleRulesTables$.pipe(filter(tables => !!tables && tables.length > 0));
            const ruleset$ = this.#rulesetStore.lifecycleRuleset$.pipe(filterNil());

            combineLatest([tables$, ruleset$])
                .pipe(take(1))
                .subscribe(([_tables, ruleset]) => {
                    const createRules = ruleset.lifecycleRules?.find(
                        ruleGroup => ruleGroup.type === 'Create'
                    )?.ruleCriteria;
                    const updateRules = ruleset.lifecycleRules?.find(
                        ruleGroup => ruleGroup.type === 'Update'
                    )?.ruleCriteria;

                    createRules?.forEach(rule => {
                        this.recordSelectionForm.controls.creationRules.push(
                            new FormGroup(new GenericSelectionCriterionForm(rule))
                        );
                    });
                    updateRules?.forEach(rule => {
                        this.recordSelectionForm.controls.updateRules.push(
                            new FormGroup(new GenericSelectionCriterionForm(rule))
                        );
                    });
                    if (!this.recordSelectionForm.controls.creationRules.value.length) {
                        this.#setRequiredCriterionFormGroup('create');
                    }
                    if (!this.recordSelectionForm.controls.updateRules.value.length) {
                        this.#setRequiredCriterionFormGroup('update');
                    }

                    const newFormValue = new FormGroup<LifecycleRulesRecordSelectionForm>(
                        new LifecycleRulesRecordSelectionForm(ruleset.lifecycleRules)
                    ).getRawValue();

                    this.recordSelectionForm.patchValue(newFormValue);

                    this.subscribeToValidationState(
                        this.recordSelectionForm,
                        'recordSelection',
                        this.#validationService,
                        this.#validationStateService
                    );
                    this.recordSelectionForm.markAllAsTouched(); // this makes the form indicate invalid fields on load
                    this.handleFormSaveWarningAfterInit();
                });
            if (this.#rulesetStore.lifecycleRuleset()?.id !== +rulesetId) {
                this.#rulesetStore.getLifecycleRuleset(+rulesetId);
            }
        } else {
            this.recordSelectionForm.patchValue(
                new FormGroup<LifecycleRulesRecordSelectionForm>(new LifecycleRulesRecordSelectionForm()).getRawValue()
            );
            this.subscribeToValidationState(
                this.recordSelectionForm,
                'recordSelection',
                this.#validationService,
                this.#validationStateService
            );
            this.recordSelectionForm.markAllAsTouched();
            this.handleFormSaveWarningAfterInit();
        }
        this.#rulesetStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#rulesetStore.resetMessage();
        });
    }

    ngAfterViewChecked(): void {
        this.#changeDetectorRef.detectChanges();
    }

    onAddUpdateCriterion() {
        this.recordSelectionForm.controls.updateRules.push(new FormGroup(new GenericSelectionCriterionForm()));
        this.recordSelectionForm.controls.updateRules.markAllAsDirty();
        this.recordSelectionForm.markAllAsDirty();
        this.recordSelectionForm.controls.updateRules.updateValueAndValidity();
        this.recordSelectionForm.controls.updateRules.markAllAsTouched();
    }

    onDeleteUpdateCriterion(index: number) {
        this.recordSelectionForm.controls.updateRules.removeAt(index);
        this.recordSelectionForm.controls.updateRules.markAllAsDirty();
        this.recordSelectionForm.markAllAsDirty();
        this.recordSelectionForm.controls.updateRules.updateValueAndValidity();
    }

    onAddCreationCriterion() {
        this.recordSelectionForm.controls.creationRules.push(new FormGroup(new GenericSelectionCriterionForm()));
        this.recordSelectionForm.controls.creationRules.markAllAsDirty();
        this.recordSelectionForm.markAllAsDirty();
        this.recordSelectionForm.controls.creationRules.updateValueAndValidity();
        this.recordSelectionForm.controls.creationRules.markAllAsTouched();
    }

    onDeleteCreationCriterion(index: number) {
        this.recordSelectionForm.controls.creationRules.removeAt(index);
        this.recordSelectionForm.controls.creationRules.markAllAsDirty();
        this.recordSelectionForm.markAllAsDirty();
        this.recordSelectionForm.controls.creationRules.updateValueAndValidity();
    }

    subscribeToValidationState(
        form: FormGroup<LifecycleRulesRecordSelectionForm>,
        sectionName: LifecycleRulesSectionName,
        validationService: ValidationService<LifecycleRulesSectionName, LifecycleRuleset, LifecycleRulesSectionFormMap>,
        validationStateService: ValidationStateService<LifecycleRulesSectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService
        );
    }

    async onSave() {
        const creationRules: GenericSelectionCriterionData[] =
            this.recordSelectionForm.controls.creationRules?.controls.map(data => {
                const table = this.lifecycleRulesTables()?.find(tbl => tbl.name === data.controls.tableName.value);
                const fields = table?.fields ?? [];
                return convertGenericSelectionCriterionForm(data, fields ?? []);
            });
        const updateRules: GenericSelectionCriterionData[] =
            this.recordSelectionForm.controls.updateRules?.controls.map(data => {
                const table = this.lifecycleRulesTables()?.find(tbl => tbl.name === data.controls.tableName.value);
                const fields = table?.fields ?? [];
                return convertGenericSelectionCriterionForm(data, fields ?? []);
            });
        const toSave: Partial<LifecycleRuleset> = {
            ...this.#rulesetStore.lifecycleRuleset(),
            id: this.#rulesetStore.lifecycleRuleset()?.id,
            updatedBy: this.loggedInUserId(),
            valid: this.#validationStateService.invalidSections().length === 0,
            lifecycleRules: [
                {
                    type: 'Create',
                    groupingLogic: {
                        groupingLogic:
                            this.recordSelectionForm.controls.creationGroupingLogic.controls.groupingLogic.value,
                        type: this.recordSelectionForm.controls.creationGroupingLogic.controls.type.value,
                    },
                    ruleCriteria: creationRules,
                },
                {
                    type: 'Update',
                    groupingLogic: {
                        groupingLogic:
                            this.recordSelectionForm.controls.updateGroupingLogic.controls.groupingLogic.value,
                        type: this.recordSelectionForm.controls.updateGroupingLogic.controls.type.value,
                    },
                    ruleCriteria: updateRules,
                },
            ],
        };

        if (!(await this.#invalidSaveWarningService.confirmSave('lifecycle rule', toSave))) {
            return noopFormSaveMutationResult();
        }
        return this.#rulesetStore.saveLifecycleRuleset(toSave).then(result => {
            if (wasSaveSuccessful(result)) {
                this.recordSelectionForm.markAsPristine();
            }
            return result;
        });
    }

    #setRequiredCriterionFormGroup(formType: 'create' | 'update') {
        const requiredCriterionForm = new FormGroup(new GenericSelectionCriterionForm());
        if (formType === 'create') {
            this.recordSelectionForm.controls.creationRules.push(requiredCriterionForm);
            this.recordSelectionForm.controls.creationRules.markAllAsDirty();
        } else if (formType === 'update') {
            this.recordSelectionForm.controls.updateRules.push(requiredCriterionForm);
            this.recordSelectionForm.controls.updateRules.markAllAsDirty();
        }
    }
}
